import * as React from 'react'
import { closeOutboundMessageModal } from '../../store/modals/actions'
import ModalHeader from './Parts/ModalHeader'
import { AppState } from '../../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import ModalWindow from './Parts/ModalWindow'
import ModalMiddle from './Parts/ModalMiddle'
import ModalContent from './Parts/ModalContent'
import ModalLoader from './Parts/ModalLoader'
import { useTranslation } from 'react-i18next'
import Button from '../Button/Button'
import { WidgetOutboundMessagesController } from '../../controllers'
import { CurrentUser, DisplayableError, WidgetOutboundMessage, WidgetOutboundMessageType } from '../../types'
import TooltipError from '../Tooltips/ErrorTooltip'
import PowerSelect from '../Form/PowerSelect'
import Notification from '../../utilities/Notification'

interface IStateToProps {
  currentUser: CurrentUser
  widgetId: string
  outboundMessageId?: string
  onSubmit: (outboundMessage: WidgetOutboundMessage) => void
}

interface IDispatchToProps {
  close: typeof closeOutboundMessageModal
}

type IProps = IDispatchToProps & IStateToProps

interface IState {
  didInitialLoad: boolean
  outboundMessage: WidgetOutboundMessage | null
  outboundMessageTypes: WidgetOutboundMessageType[]
  errors: DisplayableError[]
  isSubmitting: boolean
}

const OutboundMessageModal = (props: IProps) => {
  const { t } = useTranslation()
  const { widgetId, outboundMessageId } = props
  const [state, setState] = React.useState<IState>({
    didInitialLoad: false,
    outboundMessage: null,
    outboundMessageTypes: [],
    errors: [],
    isSubmitting: false,
  })
  const { didInitialLoad, outboundMessage, outboundMessageTypes, errors, isSubmitting } = state

  React.useEffect(() => {
    WidgetOutboundMessagesController
      .getForm(widgetId, outboundMessageId)
      .then(response => {
        setState(s => ({ ...s, outboundMessage: response.outbound_message, outboundMessageTypes: response.outbound_message_types, didInitialLoad: true }))
      })
      .catch(console.error)
  }, [])

  const typeOptions = outboundMessageTypes.map(type => ({
    label: t(`WidgetOutboundMessageType::${type}`),
    value: type as WidgetOutboundMessageType,
  }))

  const selectedTypeOption = typeOptions.find(option => option.value === outboundMessage?.type) || null

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, outboundMessage: { ...outboundMessage, name: e.currentTarget.value } })
  }

  const onTypeChange = (option) => {
    setState({ ...state, outboundMessage: { ...outboundMessage, type: option ? option.value : null } })
  }

  const onCloseClick = () => {
    props.close()
  }

  const onFormSubmit = async (e) => {
    e.preventDefault()

    if (!outboundMessage?.type) return

    setState({ ...state, isSubmitting: true })

    try {
      const action = outboundMessage.id
        ? WidgetOutboundMessagesController.update(widgetId, outboundMessage)
        : WidgetOutboundMessagesController.create(widgetId, outboundMessage)

      const response = await action

      if (response.errors) {
        setState({ ...state, errors: response.errors, isSubmitting: false })
      } else {
        Notification.notifySuccess(
          outboundMessage.id
            ? t('OutboundMessageModal::Outbound message successfully updated')
            : t('OutboundMessageModal::Outbound message successfully created')
        )
        if (props.onSubmit) props.onSubmit(response)
        props.close()
      }
    } catch (ex) {
      console.error(ex)
      setState({ ...state, isSubmitting: false })
    }
  }

  const onErrorsDismiss = () => {
    setState({ ...state, errors: [] })
  }

  return (
    <ModalWindow>
      <ModalHeader
        title={outboundMessage?.id ? t('OutboundMessageModal::Edit outbound message') : t('OutboundMessageModal::New outbound message')}
        onCloseClick={onCloseClick}
      />

      {!didInitialLoad && <ModalLoader />}
      {didInitialLoad && <ModalMiddle>
        <ModalContent>
          <form onSubmit={onFormSubmit}>
            <div className='grid'>
              <div className='grid-cell with-12col'>
                <div className='form-item'>
                  <label>{t('OutboundMessageModal::Name')}</label>
                  <input
                    type='text'
                    value={outboundMessage?.name || ''}
                    onChange={onNameChange}
                    placeholder={t('OutboundMessageModal::Enter a name')}
                  />
                </div>
              </div>
              <div className='grid-cell with-12col'>
                <div className='form-item'>
                  <label>{t('OutboundMessageModal::Type')} <span>*</span></label>
                  <PowerSelect
                    options={typeOptions}
                    value={selectedTypeOption}
                    onChange={onTypeChange}
                    placeholder={t('OutboundMessageModal::Select a type')}
                    isDisabled={!!outboundMessage?.id}
                  />
                </div>
              </div>
            </div>
            <input type='submit' style={{ display: 'none' }} onClick={onFormSubmit} />
          </form>
        </ModalContent>

        <div className='modal-footer'>
          <div />
          <div key='main-action' className='popover-wrapper'>
            <TooltipError
              errors={errors}
              onDismiss={onErrorsDismiss}
            />
            <Button
              type='success'
              text={outboundMessage?.id ? t('OutboundMessageModal::Update') : t('OutboundMessageModal::Create')}
              onClick={onFormSubmit}
              disabled={!outboundMessage?.type || isSubmitting}
            />
          </div>
        </div>
      </ModalMiddle>}
    </ModalWindow>
  )
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: { currentUser },
    modals: {
      outboundMessageModal: {
        widgetId,
        outboundMessageId,
        onSubmit,
      }
    }
  } = state

  return {
    currentUser,
    widgetId,
    outboundMessageId,
    onSubmit,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    close: () => dispatch(closeOutboundMessageModal()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(OutboundMessageModal)
